import { FC, ReactNode, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AppRoutes } from "../../common/routesUtils";
import { PageType } from "../../config/page.types";
import { UserOperatingContextDTO } from "../../datasources/api-client/data-contracts";
import { usePermissions } from "../../permissions/usePermissions";
import { getCurrentUserData } from "../../state/auth";

interface Props {
	children?: ReactNode;
	pageTypes?: PageType[];
}

/**
 * Protected component checks if the user is authenticated and allowed to access the page.
 *
 * If the user is not authenticated (i.e., no JWT token can be found locally), it
 * will redirect the user to the "Sign in" section; if the user cannot access any of the
 * given pages, it will redirect the user to the "Forbidden" error section.
 *
 * This component should be used in conjuction with routes to protect unauthorized navigation.
 *
 * @param children  the protected component
 * @param pageTypes the pages rendered by the route (a route may render more pages, e.g. by perspective);
 *                  the route is accessible if at least one of them is accessible; if undefined, only
 *                  authentication is checked
 * @returns         the protected component or will redirect user to "Sign In" / "Forbidden" app sections
 */
const Protected: FC<Props> = ({ children, pageTypes }) => {
	const userData: UserOperatingContextDTO | undefined = getCurrentUserData();
	const navigateTo = useNavigate();
	const { canAccessPage } = usePermissions();

	const isAuthenticated: boolean = !!userData?.user && !!userData.permissions && !!userData.token;

	useEffect(() => {
		if (!isAuthenticated) {
			navigateTo(AppRoutes.SignIn);
		}
	}, [navigateTo, isAuthenticated]);

	if (isAuthenticated && pageTypes && !pageTypes.some((pageType) => canAccessPage(pageType))) {
		return <Navigate to={AppRoutes.Forbidden} replace />;
	}

	return <>{children}</>;
};

export default Protected;
